import { useEffect, useState } from "react";
import { api, ApiError, type GbpAccount, type GbpLocation,
         type LinkedGbpLocation } from "../lib/api";

/**
 * Business Profile connection.
 *
 * Separate from the BigQuery sources because it behaves differently: it is
 * approval-gated, has no export, and the data is stored rather than queried
 * live. The most likely outcome on a first attempt is "not approved yet", so
 * that path is the one written most carefully.
 */
export function GbpPanel({ propertyId, connectionId, onOpenGuide }: {
  propertyId: string;
  connectionId: string | null;
  onOpenGuide: () => void;
}) {
  const [linked, setLinked] = useState<LinkedGbpLocation[]>([]);
  const [accounts, setAccounts] = useState<GbpAccount[] | null>(null);
  const [picked, setPicked] = useState<Set<string>>(new Set());
  const [busy, setBusy] = useState<string | null>(null);
  const [notApproved, setNotApproved] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [note, setNote] = useState<string | null>(null);

  const to = isoOf(Date.now() - 2 * 86400000);
  const from = isoOf(Date.now() - 180 * 86400000);

  useEffect(() => {
    api.gbpSeries(propertyId, from, to)
      .then((r) => setLinked(r.locations))
      .catch(() => {});
  }, [propertyId]);

  async function discover() {
    if (!connectionId) return;
    setBusy("discover"); setError(null); setNotApproved(false);
    try {
      const r = await api.gbpDiscover(connectionId);
      setAccounts(r.accounts);
      if (!r.accounts.some((a) => a.locations.length)) {
        setError("This Google account can see no Business Profile locations. Check it has manager or owner access to the profile.");
      }
    } catch (e) {
      const err = e as ApiError;
      if (err.code === "gbp_not_approved") setNotApproved(true);
      else setError(err.hint ?? `Could not reach Business Profile (${err.status}).`);
    } finally { setBusy(null); }
  }

  async function link() {
    if (!connectionId || !accounts) return;
    const chosen = accounts.flatMap((a) =>
      a.locations.filter((l) => picked.has(l.resourceName))
        .map((l) => ({ ...l, account: a.account.name })));
    if (!chosen.length) return;

    setBusy("link"); setError(null);
    try {
      const r = await api.gbpLink({
        property_id: propertyId, connection_id: connectionId, locations: chosen,
      });
      setLinked(r.locations);
      setAccounts(null);
      setNote(`${r.locations.length} location${r.locations.length > 1 ? "s" : ""} linked. Sync to pull metrics.`);
    } catch (e) {
      setError((e as ApiError).hint ?? "Could not link those locations.");
    } finally { setBusy(null); }
  }

  async function sync(loc: LinkedGbpLocation) {
    if (!connectionId) return;
    setBusy(loc.id); setError(null); setNote(null);
    try {
      const r = await api.gbpSync({
        connection_id: connectionId, location_id: loc.id, from, to,
      });
      setNote(`${loc.title}: ${r.days} days and ${r.keywords} keyword rows stored.`);
    } catch (e) {
      const err = e as ApiError;
      if (err.code === "gbp_not_approved") setNotApproved(true);
      else setError(err.hint ?? `Sync failed (${err.status}).`);
    } finally { setBusy(null); }
  }

  return (
    <section className="panel">
      <h2>Business Profile</h2>
      <p className="muted" style={{ maxWidth: "68ch" }}>
        Maps and local Search impressions, calls, direction requests and website
        clicks. Search Console cannot see any of this, and for a local business
        the profile often outperforms the site.
      </p>

      {notApproved && (
        <div className="warning">
          <strong>Access not granted yet.</strong> Enabling the Business Profile
          APIs leaves your quota at zero until Google approves a Basic API Access
          application. That is the normal starting state, not an error.
          <div style={{ marginTop: ".5rem" }}>
            <button className="quiet" onClick={onOpenGuide}>Open the setup guide</button>
          </div>
        </div>
      )}

      {error && <div className="warning">{error}</div>}
      {note && <p className="caveat">{note}</p>}

      {linked.length > 0 && (
        <div className="gbp-list">
          {linked.map((l) => (
            <div className="gbp-row" key={l.id}>
              <div>
                <div className="gbp-title">{l.title}</div>
                <div className="gbp-sub num">{l.resource_name}</div>
              </div>
              <button className="quiet" disabled={busy === l.id}
                      onClick={() => sync(l)}>
                {busy === l.id ? "Syncing" : "Sync"}
              </button>
            </div>
          ))}
          <p className="caveat">
            Syncing pulls the last 180 days of daily metrics and the keywords for
            every month it touches.
          </p>
        </div>
      )}

      {accounts && accounts.some((a) => a.locations.length) && (
        <div className="gbp-list">
          <div className="gbp-pick-title">Select the locations for this client</div>
          {accounts.map((a) => a.locations.map((l: GbpLocation) => (
            <label className="gbp-row pick" key={l.resourceName}>
              <input type="checkbox"
                     checked={picked.has(l.resourceName)}
                     onChange={() => setPicked((s) => {
                       const n = new Set(s);
                       n.has(l.resourceName) ? n.delete(l.resourceName) : n.add(l.resourceName);
                       return n;
                     })} />
              <div>
                <div className="gbp-title">{l.title}</div>
                <div className="gbp-sub">
                  {[l.address, l.primaryPhone].filter(Boolean).join(" · ") || l.resourceName}
                </div>
              </div>
            </label>
          )))}
          <button disabled={!picked.size || busy === "link"} onClick={link}>
            {busy === "link" ? "Linking" : `Link ${picked.size || ""} location${picked.size === 1 ? "" : "s"}`}
          </button>
        </div>
      )}

      {!accounts && (
        <button disabled={busy === "discover" || !connectionId} onClick={discover}>
          {busy === "discover" ? "Checking access"
            : linked.length ? "Find more locations" : "Connect Business Profile"}
        </button>
      )}
    </section>
  );
}

const isoOf = (t: number) => new Date(t).toISOString().slice(0, 10);
